import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Import() {
  const { isOrgAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [projectCode, setProjectCode] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setReport(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (projectCode) formData.append("project_code", projectCode);
      const { data } = await api.post("/imports/dpr-workbook", formData);
      setReport(data);
      toast.success(`Workbook imported into project ${data.project_code}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  if (!isOrgAdmin) {
    return <p className="text-sm text-muted-foreground" data-testid="import-forbidden">Only an organisation administrator can import a DPR workbook.</p>;
  }

  return (
    <div className="space-y-6" data-testid="import-page">
      <PageHeader title="Workbook Migration" description="Upload a legacy DPR .xlsx / .xlsm workbook to create or update a project." />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="workbook-dropzone"
        className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-12 text-center transition-colors ${dragging ? "border-gold-500 bg-gold-500/5" : "border-border bg-card"}`}
      >
        <UploadCloud className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">{file ? file.name : "Drag & drop your workbook, or click to browse"}</p>
        <p className="text-xs text-muted-foreground">.xlsx or .xlsm only</p>
        <input
          ref={inputRef}
          type="file"
          data-testid="workbook-dropzone-input"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Project code (optional — blank creates a new project)</Label>
          <Input data-testid="import-project-code-input" value={projectCode} onChange={(e) => setProjectCode(e.target.value)} className="mt-1.5 w-56" placeholder="PRJ-001" />
        </div>
        <Button onClick={submit} disabled={!file || uploading} data-testid="import-submit-button">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          Import workbook
        </Button>
      </div>

      {report && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="import-report">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            Import report — project <span className="font-mono text-gold-600 dark:text-gold-400">{report.project_code}</span>
          </h2>
          <div className="space-y-3">
            {report.sheets.map((s) => (
              <div key={s.sheet} className="rounded-lg border border-border p-3" data-testid={`import-sheet-${s.sheet}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{s.sheet}</p>
                  <div className="flex gap-3 font-mono text-xs text-muted-foreground">
                    <span>read {s.rows_read}</span>
                    <span className="text-emerald-400">created {s.created}</span>
                    <span className="text-sky-400">updated {s.updated}</span>
                    <span className="text-muted-foreground">skipped {s.skipped}</span>
                  </div>
                </div>
                {s.warnings?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {s.warnings.map((w, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gold-400">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                )}
                {(!s.warnings || s.warnings.length === 0) && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3 w-3" /> No warnings</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
